import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import withStoreAuth from "../../../auth/StoreRouteAuth";
import StoreLayout from "./StoreLayout";
import {Badge, Btn, Dialog, Field, Loading, Notice, Panel, SectionHead} from "./StoreUI";
import {storeGet, storePost, storeLogout, STORE_LOGIN_PATH} from "../../../services/storeApi";
import {TbLock} from "react-icons/tb";

/**
 * ════════════════════════════════════════════════════════════════════════════════
 *  상점사(쇼핑몰) 내정보 — 정보수정 / 비밀번호 변경 / 회원탈퇴
 * ════════════════════════════════════════════════════════════════════════════════
 *  경로 : /store/api/modify
 *
 *  [최초 1회 계정정보 채우기]
 *   마운트 시 /store/myInfo 를 한 번 조회해 이메일·상호명·자사URL·담당자·연락처를
 *   모든 입력칸에 그대로 채운다.
 *    - 이메일  : 계정 식별자라 변경할 수 없다(자물쇠 표시 + disabled)
 *    - 나머지  : 조회값이 채워진 상태에서 바로 수정할 수 있다
 *   불러오는 동안에는 빈 칸이 잠깐 보이지 않도록 로딩을 먼저 보여준다.
 *   (연락처는 서버가 복호화해서 내려준다 — StoreMemberService.toDto)
 * ════════════════════════════════════════════════════════════════════════════════
 */
function StoreModifyPg() {

    const navigate = useNavigate();

    const [info, setInfo] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState('');

    const [companyName, setCompanyName] = useState('');
    const [name, setName] = useState('');
    const [storeUrl, setStoreUrl] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    const [confirmWithdraw, setConfirmWithdraw] = useState(false);
    const [withdrawing, setWithdrawing] = useState(false);

    const PASSWORD_REGEX = /^(?=.*[a-zA-Z])(?=.*[!@#$%^&*()])(?=.*[0-9]).{8,20}$/;

    /** 조회한 계정정보를 모든 입력칸에 채운다 */
    const fillForm = (d) => {
        setInfo(d);
        setCompanyName(d?.companyName || '');
        setName(d?.name || '');
        setStoreUrl(d?.storeUrl || '');
        setPhone(d?.phone || '');
    };

    useEffect(() => {
        let alive = true;
        storeGet('/store/myInfo')
            .then(res => {
                if (!alive) return;
                fillForm(res.data);
                setLoadError('');
            })
            .catch(() => {
                if (!alive) return;
                setInfo(null);
                setLoadError('계정정보를 불러오지 못했습니다. 새로고침 후 다시 시도해주세요.');
            })
            .finally(() => {
                if (alive) setLoaded(true);
            });
        return () => {
            alive = false;
        };
    }, []);

    const validate = () => {
        const next = {};
        if (!companyName.trim()) next.companyName = '상호명을 입력해주세요';
        if (!storeUrl.trim()) next.storeUrl = '자사 URL을 입력해주세요';
        if (!name.trim()) next.name = '담당자 성함을 입력해주세요';
        if (!phone || phone.length < 9) next.phone = '담당자 연락처를 정확히 입력해주세요';
        // 비밀번호는 입력했을 때만 검증(미입력 시 변경하지 않음)
        if (password) {
            if (!PASSWORD_REGEX.test(password)) next.password = '영문·숫자·특수문자를 포함한 8~20자로 입력해주세요';
            else if (password !== passwordConfirm) next.passwordConfirm = '비밀번호가 일치하지 않습니다';
        }
        setErrors(next);
        return Object.values(next).every(v => !v);
    };

    const handleSave = async () => {
        if (saving) return;
        setSavedMsg('');
        if (!validate()) return;

        setSaving(true);
        try {
            const res = await storePost('/store/modifyInfo', {
                companyName: companyName.trim(),
                storeUrl: storeUrl.trim(),
                name: name.trim(),
                phone,
                password: password || undefined,
            });
            // 저장 결과를 다시 화면에 반영해 서버 값과 항상 일치시킨다
            fillForm(res.data);
            setPassword('');
            setPasswordConfirm('');
            setSavedMsg('저장했습니다');
            setTimeout(() => setSavedMsg(''), 2400);
        } catch (e) {
            setErrors({submit: e?.response?.data?.message || '저장하지 못했습니다. 잠시 후 다시 시도해주세요.'});
        } finally {
            setSaving(false);
        }
    };

    const handleWithdraw = async () => {
        if (withdrawing) return;
        setWithdrawing(true);
        try {
            await storePost('/store/withdraw');
            storeLogout();
            navigate(STORE_LOGIN_PATH, {replace: true});
        } catch (e) {
            setConfirmWithdraw(false);
            setErrors({submit: '탈퇴 처리에 실패했습니다'});
        } finally {
            setWithdrawing(false);
        }
    };

    return (
        <StoreLayout active="modify"
                     eyebrow="Account"
                     title="내정보"
                     desc="상점 정보와 비밀번호를 바꾸실 수 있습니다">

            {!loaded ? (
                <Panel className="st-rise st-rise-3 tw-max-w-[600px] tw-p-6">
                    <Loading label="계정정보를 불러오는 중"/>
                </Panel>
            ) : (
                <>
                    <Panel className="st-rise st-rise-3 tw-max-w-[600px] tw-p-6">
                        <SectionHead
                            eyebrow="Profile"
                            title="상점 정보"
                            desc="비토리지가 연락드릴 때 사용하는 정보입니다"
                            right={info?.activateFl === true
                                ? <Badge tone="sage" dot>활성</Badge>
                                : <Badge tone="blush" dot>비활성</Badge>}
                        />

                        {loadError ? <div className="tw-mb-4"><Notice tone="warn">{loadError}</Notice></div> : null}

                        {/* 이메일 — 계정 식별자라 변경 불가 */}
                        <Field label="이메일" hint="계정을 구분하는 값이라 바꿀 수 없습니다">
                            <div className="tw-relative">
                                <input className="st-input st-mono tw-pr-11" type="text"
                                       value={info?.email || ''} disabled readOnly aria-label="이메일 (변경불가)"/>
                                <TbLock aria-hidden="true"
                                        className="tw-pointer-events-none tw-absolute tw-right-4 tw-top-1/2 tw--translate-y-1/2 tw-text-[16px] tw-text-[color:var(--st-text-faint)]"/>
                            </div>
                        </Field>

                        <Field label="상호명" error={errors.companyName} required>
                            <input className="st-input" type="text" value={companyName} maxLength={50}
                                   placeholder="사업자 상호명을 입력해주세요"
                                   onChange={(e) => setCompanyName(e.target.value)}/>
                        </Field>

                        <Field label="자사 URL" error={errors.storeUrl} required
                               hint="앱 서비스라면 앱 이름을 적어주세요">
                            <input className="st-input" type="text" value={storeUrl} maxLength={500}
                                   placeholder="https://mystore.com"
                                   onChange={(e) => setStoreUrl(e.target.value)}/>
                        </Field>

                        <Field label="담당자 성함" error={errors.name} required>
                            <input className="st-input" type="text" value={name} maxLength={30}
                                   placeholder="담당자 성함을 입력해주세요"
                                   onChange={(e) => setName(e.target.value)}/>
                        </Field>

                        <Field label="담당자 연락처" error={errors.phone} required>
                            <input className="st-input st-num" type="text" value={phone} maxLength={12} inputMode="numeric"
                                   placeholder="'-' 없이 숫자만 입력해주세요"
                                   onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 12))}/>
                        </Field>

                        {/*─── 비밀번호 ───*/}
                        <div className="tw-mt-7 tw-border-t tw-pt-6" style={{borderColor: 'var(--st-glass-line)'}}>
                            <p className="st-eyebrow tw-mb-4">Password</p>

                            <Field label="새 비밀번호" error={errors.password}
                                   hint="바꾸지 않으시려면 비워두세요">
                                <input className="st-input" type="password" value={password} maxLength={20}
                                       placeholder="영문·숫자·특수문자 포함 8~20자"
                                       onChange={(e) => setPassword(e.target.value)}/>
                            </Field>

                            <Field label="새 비밀번호 확인" error={errors.passwordConfirm}>
                                <input className="st-input" type="password" value={passwordConfirm} maxLength={20}
                                       placeholder="새 비밀번호를 한 번 더 입력해주세요"
                                       onChange={(e) => setPasswordConfirm(e.target.value)}/>
                            </Field>
                        </div>

                        {errors.submit ? <div className="tw-mb-3"><Notice tone="warn">{errors.submit}</Notice></div> : null}
                        {savedMsg ? <div className="tw-mb-3"><Notice tone="done">{savedMsg}</Notice></div> : null}

                        <Btn tone="dawn" size="lg" className="tw-mt-2 tw-w-full" onClick={handleSave}
                             disabled={saving} busy={saving}>
                            변경사항 저장
                        </Btn>
                    </Panel>

                    {/*─── 탈퇴 ───*/}
                    <Panel className="st-rise st-rise-4 tw-mt-4 tw-max-w-[600px] tw-p-6">
                        <SectionHead eyebrow="Danger zone" title="연동 종료"
                                     desc="탈퇴하면 API 키가 폐기되고 쇼핑몰의 비토리지 버튼이 멈춥니다"/>
                        <Btn tone="blush" size="md" onClick={() => setConfirmWithdraw(true)}>회원탈퇴</Btn>
                    </Panel>
                </>
            )}

            {/*탈퇴 확인*/}
            {confirmWithdraw ? (
                <Dialog title="정말 탈퇴하시겠습니까?"
                        confirmLabel="탈퇴하기" tone="blush" busy={withdrawing}
                        onCancel={() => setConfirmWithdraw(false)}
                        onConfirm={handleWithdraw}>
                    탈퇴하면 발급된 API 키가 즉시 폐기되어 쇼핑몰에 삽입된 비토리지 버튼이 동작하지 않습니다.
                    이미 진행중인 구매대행 주문에 대한 의무는 그대로 유지됩니다.
                </Dialog>
            ) : null}
        </StoreLayout>
    );
}

export default withStoreAuth(StoreModifyPg);
